import { datadogRum, RumEvent } from '@datadog/browser-rum';

const packageJson = require('../../../package.json');

export const SERVICE_NAME = 'nocodenodeweb';
export const APPLICATION_ID = {
  PRODUCTION: '0fe5d467-797b-412c-9298-926f9e9f38c1',
  SANDBOX: '89a547d3-6d6c-435b-a5d6-08d99dd236cd',
  QA: 'a35d3113-10a8-4627-8047-cdfd0b525d1f'
};
export const CLIENT_ID = {
  PRODUCTION: 'pub0d7fd9504d3dcea8fac2bf1a9db36ff2',
  SANDBOX: 'pub81376fccac5c9d506846d0611f2f06cf',
  QA: 'pubeca4ff0cb4f336c706f3a8089383c20a'
};

const DATADOG_RUM_SITE = 'us5.datadoghq.com';

function getClientEnvironment() {
  const hostname = typeof window !== 'undefined' ? window.location.host : '';
  if (hostname.includes('localhost')) return 'DEVELOPMENT';
  if (hostname.includes('qa.paypal.com')) return 'STAGE';
  if (hostname.includes('sandbox.paypal.com')) return 'SANDBOX';
  if (hostname.includes('paypal.com')) return 'PRODUCTION';

  return undefined;
}

function getDDTraceLink(correlationId: string) {
  const environment = getClientEnvironment();
  switch (environment) {
    case 'PRODUCTION':
      return `https://go/ddprod/trace?${correlationId}`;
    case 'SANDBOX':
      return `https://go/ddsb/trace?${correlationId}`;
    default:
      return `https://go/ddqa/trace?${correlationId}`;
  }
}

function getCalLogLink(correlationId: string) {
  const environment = getClientEnvironment();
  switch (environment) {
    case 'PRODUCTION':
      return `https://go/ddprod/tocal?${correlationId}`;
    case 'SANDBOX':
      return `https://go/ddsb/tocal?${correlationId}`;
    default:
      return `https://go/ddqa/tocal?${correlationId}`;
  }
}

export function getApplicationId() {
  const environment = getClientEnvironment();
  switch (environment) {
    case 'PRODUCTION':
      return APPLICATION_ID.PRODUCTION;
    case 'SANDBOX':
      return APPLICATION_ID.SANDBOX;
    default:
      return APPLICATION_ID.QA;
  }
}

export function getClientToken() {
  const environment = getClientEnvironment();
  switch (environment) {
    case 'PRODUCTION':
      return CLIENT_ID.PRODUCTION;
    case 'SANDBOX':
      return CLIENT_ID.SANDBOX;
    default:
      return CLIENT_ID.QA;
  }
}

export function addCorrelationIdToContext(event: RumEvent, correlationId: string | null) {
  if (correlationId && typeof correlationId === 'string') {
    event.context = event.context || {};
    event.context.correlationId = correlationId;

    const ddTraceLink = getDDTraceLink(correlationId);
    const calLogLink = getCalLogLink(correlationId);
    event.context['dd-trace-url'] = ddTraceLink;
    event.context['external-cal-url'] = calLogLink;
  }
}

/* istanbul ignore next */
if (typeof window !== 'undefined') {
  // init Datadog RUM
  /* istanbul ignore next */
  datadogRum.init({
    // properties for identifying and authenticating the application with RUM
    applicationId: getApplicationId(),
    clientToken: getClientToken(),
    site: DATADOG_RUM_SITE,
    service: SERVICE_NAME,
    version: packageJson.version,
    // sampling must be set at 100 for session and 0 for recording. retention will be controlled on the backend in Datadog
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    // auto-instrumentations https://docs.datadoghq.com/real_user_monitoring/browser/data_collected/
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // privacy defaults to prevent leaking PII
    defaultPrivacyLevel: 'mask',
    enablePrivacyForActionName: true,
    // exclude log, tealeaf, and fpti endpoints which are not tied to user interactions from activity tracking
    excludedActivityUrls: [/log/i, /fpti/i, /tealeaf/i, /tracking\/ts/i],
    // exclude log, tealeaf, and fpti endpoints which are not tied to user interactions from universal tracing
    allowedTracingUrls: [
      (url) =>
        !(
          url.toLowerCase().includes('log') ||
          url.toLowerCase().includes('fpti') ||
          url.toLowerCase().includes('tealeaftarget') ||
          url.toLowerCase().includes('tracking/ts')
        ) && url.includes('paypal.com')
    ],
    beforeSend(event, context) {
      // modify RUM events to include the correlationId and links to logs and traces
      // for the request because we don't yet have universal tracing
      if (event.type === 'resource') {
        // handle fetch
        if ('response' in context && context?.response?.headers) {
          const correlationId = context.response.headers.get('Paypal-Debug-Id');
          addCorrelationIdToContext(event, correlationId);
        }
        // handle XHR
        if (
          'xhr' in context &&
          context.xhr &&
          'getResponseHeader' in context.xhr &&
          typeof context.xhr.getResponseHeader === 'function'
        ) {
          const correlationId = context.xhr.getResponseHeader('Paypal-Debug-Id');
          addCorrelationIdToContext(event, correlationId);
        }
      }
      return true;
    }
  });
}
